/* eslint-disable */

import React, { useEffect, useState, useMemo } from 'react'
import { Container, ContentHeader, Title, ActionToolbar, ContentBody, EatersGrid, EaterCard, EaterContent, EaterInfo, EaterActions, SearchContainer, PaginationContainer, PaginationInfo, PaginationControls, PageSizeControl } from './Eaters.styled';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { fetchEaters, setPageSize, setCurrentPage, selectPageSize, selectCurrentPage, selectRowCount, selectShouldOpen, selectEaters, selectMessage, selectSeverity, setOpen, blockUser } from './slice';
import PageContainer from '../../components/PageContainer/PageContainer';
import { useNavigate } from 'react-router-dom';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import { Eater } from './types'
import { Button, TextField, Select, MenuItem, InputLabel, FormControl } from '@mui/material';

const Eaters: React.FC = () => {
  const openServer = useAppSelector(selectShouldOpen);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const eatersFromServer = useAppSelector(selectEaters);
  const rowCount = useAppSelector(selectRowCount);
  const pageSize = useAppSelector(selectPageSize);
  const currentPage = useAppSelector(selectCurrentPage);
  const [eaters, setEaters] = useState(eatersFromServer);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(()=>{
    setEaters(eatersFromServer)
  },[eatersFromServer])

  useEffect(() => {
    dispatch(fetchEaters(currentPage));
  }, [dispatch, currentPage, pageSize])

  const handleBlockUser = (id: string, event: React.MouseEvent): void => {
    event.stopPropagation();
    try {
      dispatch(blockUser(id))
      .then(() => {
        dispatch(fetchEaters(0));
      })
      .catch(err => err)
    } catch (e) {
      console.log(e);
    }
  }

  const setOpenServer = (open: boolean) => {
    dispatch(setOpen(open));
  }

  const message = useAppSelector(selectMessage);
  const severity = useAppSelector(selectSeverity);

  // Filter eaters based on search query
  const filteredEaters = useMemo(() => {
    if (!searchQuery.trim()) {
      return eaters;
    }
    const lowerQuery = searchQuery.toLowerCase();
    return eaters.filter(eater =>
      (eater.firstName?.toLowerCase().includes(lowerQuery) ?? false) ||
      (eater.lastName?.toLowerCase().includes(lowerQuery) ?? false) ||
      (eater.email?.toLowerCase().includes(lowerQuery) ?? false) ||
      (eater.city?.toLowerCase().includes(lowerQuery) ?? false) ||
      (eater.state?.toLowerCase().includes(lowerQuery) ?? false)
    );
  }, [eaters, searchQuery]);

  const totalPages = Math.ceil(rowCount / pageSize);
  const startIndex = currentPage * pageSize + 1;
  const endIndex = Math.min((currentPage + 1) * pageSize, rowCount);
  const hasNextPage = currentPage < totalPages - 1;
  const hasPreviousPage = currentPage > 0;

  return (
    <PageContainer title='Eaters' open={openServer} setOpen={setOpenServer} message={message} severity={severity}>
      <Container>
        <ContentHeader>
          <Title>Eaters</Title>
          <ActionToolbar>
            <Button
              variant="contained"
              size="small"
              onClick={() => navigate('/eaters/add-eater')}
            >
              Add Eater
            </Button>
          </ActionToolbar>
        </ContentHeader>

        <SearchContainer>
          <TextField
            placeholder="Search by name, email, city, or state..."
            variant="outlined"
            size="small"
            fullWidth
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </SearchContainer>

        <ContentBody>
          <EatersGrid>
            {filteredEaters.map((eater) => (
              <EaterCard key={eater.eaterId} onClick={() => navigate(`/eaters/${eater.eaterId}`)}>
                <EaterContent>
                  <EaterInfo>
                    <div><strong>{eater.firstName} {eater.lastName}</strong></div>
                    <div>{eater.email}</div>
                    {eater.city && eater.state && (
                      <div>{eater.city}, {eater.state}</div>
                    )}
                    <div>Reviews: {eater.reviews?.length ?? 0}</div>
                    <div>Status: <strong>{eater.status === 'BLOCKED' ? 'BLOCKED' : 'Active'}</strong></div>
                  </EaterInfo>
                </EaterContent>
                <EaterActions>
                  {eater.status !== 'BLOCKED' && (
                    <Button
                      data-testid="block-button"
                      variant="outlined"
                      size="small"
                      color="error"
                      onClick={(e) => handleBlockUser(eater.eaterId, e)}
                    >
                      Block User
                    </Button>
                  )}
                </EaterActions>
              </EaterCard>
            ))}
          </EatersGrid>
        </ContentBody>

        <PaginationContainer>
          <PaginationInfo>
            Showing {rowCount > 0 ? startIndex : 0} to {endIndex} of {rowCount} eaters
          </PaginationInfo>

          <PaginationControls>
            <Button
              variant="outlined"
              size="small"
              onClick={() => dispatch(setCurrentPage(currentPage - 1))}
              disabled={!hasPreviousPage}
            >
              Previous
            </Button>
            <PaginationInfo>
              Page {currentPage + 1} of {totalPages}
            </PaginationInfo>
            <Button
              variant="outlined"
              size="small"
              onClick={() => dispatch(setCurrentPage(currentPage + 1))}
              disabled={!hasNextPage}
            >
              Next
            </Button>
          </PaginationControls>

          <PageSizeControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Items Per Page</InputLabel>
              <Select
                value={pageSize}
                label="Items Per Page"
                onChange={(e) => {
                  dispatch(setPageSize(Number(e.target.value)));
                  dispatch(setCurrentPage(0));
                }}
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
            </FormControl>
          </PageSizeControl>
        </PaginationContainer>
      </Container>
    </PageContainer>
  )
}

export default withAuthenticationRequired(Eaters)
